import {type ColorMode, getClientTheme, getPreferredColorMode, setClientMode, setClientTheme} from '../color-modes'
import {controller, targets} from '@github/catalyst'
import {
  AppearanceFormElementChangeEvent,
  type ContrastSettingChangeEvent,
} from '@github-ui/appearance-settings-page/events'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'

type ContrastSettingValue = 'enabled' | 'disabled'

/** Dispatch event when theme selection changes */
function setContrastMode(theme: string, type: ColorMode) {
  const contrastSettings: {light?: ContrastSettingValue; dark?: ContrastSettingValue} = {
    [type]: theme.endsWith('_high_contrast') ? 'enabled' : 'disabled',
  }
  const changeEvent = new AppearanceFormElementChangeEvent(contrastSettings)
  window.dispatchEvent(changeEvent)
}

@controller('appearance-form')
class AppearanceFormElement extends HTMLElement {
  @targets declare modePanels: HTMLElement[]

  connectedCallback() {
    window.addEventListener('ContrastSetting:change', this.contrastChanged.bind(this))
  }

  modeSelectChange(event: Event) {
    const select = event.currentTarget as HTMLSelectElement
    const modeName = select.value
    const form = this.activatePanel(modeName)!
    // Store whether the user has selected a single theme
    // This is used in 'appearance.scss' to hide mode toggles when a single theme is selected.
    this.setAttribute('data-theme-mode', modeName)
    const contrastSettings = {
      light: (getClientTheme('light')?.endsWith('_high_contrast') ? 'enabled' : 'disabled') as ContrastSettingValue,
      dark: (getClientTheme('dark')?.endsWith('_high_contrast') ? 'enabled' : 'disabled') as ContrastSettingValue,
    }
    if (modeName === 'single' && (contrastSettings.light === 'enabled' || contrastSettings.dark === 'enabled')) {
      contrastSettings.light = 'enabled'
      contrastSettings.dark = 'enabled'
    }
    this.activateForm(form, contrastSettings)
  }

  activatePanel(modeName: string) {
    let activeForm
    for (const panel of this.modePanels) {
      const hidden = this.modePanels.length > 1 ? panel.getAttribute('data-mode-panel') !== modeName : false
      panel.hidden = hidden
      if (!hidden) {
        const form = panel.querySelector('form') as HTMLFormElement
        if (form) {
          activeForm = form
          form.setAttribute('id', 'appearance-form') // used to trigger form updated in response to `ContrastSetting` changes
        }
      } else {
        const form = panel.querySelector('form') as HTMLFormElement
        if (form && form.getAttribute('id') === 'appearance-form') {
          form.removeAttribute('id')
        }
      }
    }
    return activeForm
  }

  activateForm(form: HTMLFormElement, contrastSettings: {light?: ContrastSettingValue; dark?: ContrastSettingValue}) {
    // Append `_high_contrast` to radio button values when the user has opted for increased contrast
    for (const input of form.querySelectorAll<HTMLInputElement>('input[type="radio"]')) {
      const mode = input.getAttribute('data-type') as ColorMode | null
      const theme = input.getAttribute('value')
      if (!mode || !theme || contrastSettings[mode] === undefined) {
        continue
      }
      let newTheme = theme.replace(/_high_contrast$/, '')
      if (contrastSettings[mode] === 'enabled') {
        newTheme = `${newTheme}_high_contrast`
      }
      input.setAttribute('value', newTheme)
      // In addition to updating radio button values, update <html>’s data attributes.
      if (getClientTheme(mode) === theme) {
        setClientTheme(newTheme, mode)
      }
    }
    const selectedInputs = form.querySelectorAll<HTMLInputElement>('input[type="radio"]:checked')
    if (selectedInputs.length === 0) {
      const currentMode = getPreferredColorMode()
      const userTheme = getClientTheme(currentMode as ColorMode)
      // Find the current theme’s radio button.
      // If the user is using an increased-contrast theme, this will be the default-contrast theme’s radio button.
      const input = form.querySelector<HTMLInputElement>(
        `input[value=${userTheme}],input[value=${userTheme?.replace(/_high_contrast$/, '')}]`,
      )
      if (input) {
        input.checked = true
        this.changeTheme(input)
      }
    } else {
      for (const selectedInput of selectedInputs) {
        this.changeTheme(selectedInput)
      }
    }
    // This prevents Firefox from warning about untrusted events
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit()
    } else {
      form.dispatchEvent(new Event('submit'))
    }
  }

  /** Event handler for `ContrastSetting` updates. This keeps `appearance-form-element` in-sync with `ContrastSetting`. */
  contrastChanged(event: ContrastSettingChangeEvent) {
    const contrastSettings = event.detail
    const form = this.querySelector<HTMLFormElement>('#appearance-form')
    if (form) {
      this.activateForm(form, contrastSettings)
    }
  }

  themeClicked(event: Event) {
    const selectedInput = event.currentTarget as HTMLInputElement
    this.changeTheme(selectedInput)
  }

  changeTheme(input: HTMLInputElement) {
    const theme = input.value
    const themeType = input.getAttribute('data-type')
    const mode = input.getAttribute('data-mode')

    if (theme && themeType && mode) {
      setClientMode(mode as ColorMode)
      setClientTheme(theme, themeType as ColorMode)
      setContrastMode(theme, themeType as ColorMode)
    }
  }

  async saveTheme(event: Event) {
    event.preventDefault()

    const form = event.currentTarget as HTMLFormElement
    const formData = new FormData(form)
    await fetch(form.action, {
      method: form.method,
      body: formData,
      headers: {...getBaseFetchHeaders()},
    })
  }
}
